import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, ImageOff, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/contact";
import { Logo } from "@/components/site/Header";

const TOKEN_KEY = "rk_admin_token";
const STATUS = { uj: "Új", visszahivva: "Visszahívva", elszallitva: "Elszállítva" };
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const inputCls = "border-2 border-ink bg-white p-3 outline-none focus:border-transport";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data.token);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Sikertelen bejelentkezés");
    }
  };
  return (
    <div className="min-h-screen grid place-items-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm grid gap-4 border-2 border-ink p-6 bg-white">
        <div className="mo flex items-center gap-2"><Logo /> Admin</div>
        <label className="grid gap-1 lb">E-mail<input data-testid="admin-email-input" className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label className="grid gap-1 lb">Jelszó<input data-testid="admin-password-input" className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        {err && <p data-testid="admin-login-error" className="text-red-700 font-bold">{err}</p>}
        <button data-testid="admin-login-submit" className="btn-press mo bg-signal text-ink py-3 border-2 border-ink">Belépés</button>
      </form>
    </div>
  );
};

const PhotoThumb = ({ lead, token, onOpen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!lead.photo_path) return undefined;
    let obj;
    axios.get(`${API}/admin/leads/${lead.id}/photo`, { ...auth(token), responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => setUrl(null));
    return () => obj && URL.revokeObjectURL(obj);
  }, [lead, token]);
  if (!lead.photo_path) return <ImageOff className="w-6 h-6 text-slatetext" aria-label="Nincs kép" />;
  if (!url) return <span className="lb">Betöltés…</span>;
  return (
    <button type="button" onClick={() => onOpen(url)} data-testid={`lead-photo-${lead.id}`}>
      <img src={url} alt={`Kanapé fotó – ${lead.name}`} className="w-20 h-16 object-cover border-2 border-ink" />
    </button>
  );
};

const LeadRow = ({ lead, token, onStatus, onOpen }) => (
  <tr data-testid={`lead-row-${lead.id}`} className="border-b-2 border-ink align-middle">
    <td className="p-3 lb whitespace-nowrap">{new Date(lead.created_at).toLocaleString("hu-HU")}</td>
    <td className="p-3 font-bold">{lead.name}</td>
    <td className="p-3"><a href={`tel:${lead.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1 text-transport font-bold"><Phone className="w-4 h-4" />{lead.phone}</a></td>
    <td className="p-3">{lead.floor}</td>
    <td className="p-3"><PhotoThumb lead={lead} token={token} onOpen={onOpen} /></td>
    <td className="p-3">
      <Select value={lead.status} onValueChange={(v) => onStatus(lead.id, v)}>
        <SelectTrigger data-testid={`lead-status-${lead.id}`} className="w-40 rounded-none border-2 border-ink bg-white"><SelectValue /></SelectTrigger>
        <SelectContent className="rounded-none">{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </td>
  </tr>
);

const Leads = ({ token, onLogout }) => {
  const [leads, setLeads] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/leads`, auth(token)).then((r) => setLeads(r.data))
      .catch((e) => (e?.response?.status === 401 ? onLogout() : toast.error("Nem sikerült betölteni")));
  }, [token, onLogout]);
  useEffect(() => { load(); }, [load]);
  const onStatus = async (id, status) => {
    await axios.patch(`${API}/admin/leads/${id}`, { status }, auth(token));
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    toast.success("Állapot frissítve");
  };
  return (
    <div className="px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="mo text-3xl">Beérkezett kérések</h1>
        <button onClick={onLogout} data-testid="admin-logout-button" className="btn-press mo inline-flex items-center gap-2 px-4 py-2 bg-ink text-white"><LogOut className="w-4 h-4" />Kilépés</button>
      </div>
      {leads === null ? <p className="lb">Betöltés…</p> : leads.length === 0 ? (
        <p data-testid="admin-empty" className="mo text-xl border-2 border-ink bg-white p-8">Még nincs beérkezett kérés.</p>
      ) : (
        <div className="overflow-x-auto border-2 border-ink bg-white">
          <table data-testid="admin-leads-table" className="w-full text-left">
            <thead className="bg-ink text-white lb"><tr>{["Időpont", "Név", "Telefon", "Emelet / lift", "Fotó", "Állapot"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
            <tbody>{leads.map((l) => <LeadRow key={l.id} lead={l} token={token} onStatus={onStatus} onOpen={setPreview} />)}</tbody>
          </table>
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl rounded-none border-2 border-ink">
          <DialogTitle className="mo">Kanapé fotó</DialogTitle>
          {preview && <img src={preview} alt="Kanapé fotó nagyítva" className="w-full max-h-[75vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const login = (t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); };
  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }, []);
  return (
    <div className="dir-3 font-body bg-ground text-ink min-h-screen">
      {token ? <Leads token={token} onLogout={logout} /> : <Login onLogin={login} />}
    </div>
  );
}
